import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { Users, ShoppingCart, FileText, DollarSign, TrendingUp, TrendingDown, CheckCircle2, Clock, AlertTriangle, Phone, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";

const CHANNEL_COLORS = {
  Retail: "hsl(12, 76%, 61%)",
  GDO: "hsl(173, 58%, 39%)",
  DO: "hsl(197, 37%, 24%)",
  "Ho.Re.Ca.": "hsl(43, 74%, 66%)",
};

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [revenueByClient, setRevenueByClient] = useState(null);
  const [revenueByRegion, setRevenueByRegion] = useState(null);
  const [revenueByAgent, setRevenueByAgent] = useState(null);
  const [activities, setActivities] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [dashboardRes, clientRes, regionRes] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/analytics/revenue-by-client"),
        api.get("/analytics/revenue-by-region"),
      ]);

      setAnalytics(dashboardRes.data);
      setRevenueByClient(clientRes.data);
      setRevenueByRegion(regionRes.data);

      if (isAdmin) {
        const agentRes = await api.get("/analytics/revenue-by-agent");
        setRevenueByAgent(agentRes.data);
      }

      // Fetch activities for admin and agents
      if (user?.role === "admin" || user?.role === "agent") {
        const activitiesRes = await api.get("/activities/dashboard/summary");
        setActivities(activitiesRes.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-80 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const channelData = Object.entries(analytics?.revenue_by_channel || {}).map(
    ([name, value]) => ({
      name,
      value,
      color: CHANNEL_COLORS[name] || "hsl(200, 50%, 50%)",
    })
  );

  const monthlyData = Object.entries(analytics?.revenue_by_month || {}).map(
    ([month, value]) => ({
      month: month.slice(5),
      value,
    })
  );

  const clientData = Object.entries(revenueByClient?.revenue_by_client || {})
    .slice(0, 5)
    .map(([name, value]) => ({ name: name.slice(0, 20), value }));

  const regionData = Object.entries(revenueByRegion?.revenue_by_region || {}).map(
    ([name, value]) => ({ name, value })
  );

  const agentData = Object.entries(revenueByAgent?.revenue_by_agent || {}).map(
    ([name, value]) => ({
      name,
      revenue: value,
      commission: revenueByAgent?.commissions_by_agent?.[name] || 0,
    })
  );

  const isClient = user?.role === "client";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">
            Benvenuto, {user?.name}
          </h1>
          <p className="text-muted-foreground">
            {isClient ? "Ecco un riepilogo dei tuoi ordini" : "Ecco un riepilogo delle tue performance"}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card animate-slide-up" style={{ animationDelay: "0ms" }}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{isClient ? "Totale Speso" : "Fatturato Totale"}</p>
                <p className="stat-card-value text-accent" data-testid="total-revenue">
                  {formatCurrency(analytics?.total_revenue || 0)}
                </p>
              </div>
              <div className="stat-card-icon bg-accent/10">
                <DollarSign className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        {!isClient && (
          <Card className="stat-card animate-slide-up" style={{ animationDelay: "50ms" }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clienti</p>
                  <p className="stat-card-value" data-testid="clients-count">
                    {analytics?.clients_count || 0}
                  </p>
                </div>
                <div className="stat-card-icon bg-blue-500/10">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="stat-card animate-slide-up" style={{ animationDelay: "100ms" }}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ordini</p>
                <p className="stat-card-value" data-testid="orders-count">
                  {analytics?.orders_count || 0}
                </p>
              </div>
              <div className="stat-card-icon bg-green-500/10">
                <ShoppingCart className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card animate-slide-up" style={{ animationDelay: "150ms" }}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Preventivi</p>
                <p className="stat-card-value" data-testid="quotes-count">
                  {analytics?.quotes_count || 0}
                </p>
              </div>
              <div className="stat-card-icon bg-purple-500/10">
                <FileText className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Activities Section - Admin/Agent Only */}
      {!isClient && activities && (activities.overdue?.length > 0 || activities.due_today?.length > 0 || activities.upcoming?.length > 0) && (
        <Card className="animate-slide-up border-l-4 border-l-amber-500" style={{ animationDelay: "175ms" }}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Attività da Completare
                {activities.total_pending > 0 && (
                  <Badge variant="secondary" className="ml-2">{activities.total_pending}</Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Overdue Activities */}
              {activities.overdue?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-600 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    In Ritardo ({activities.overdue.length})
                  </h4>
                  <div className="space-y-2">
                    {activities.overdue.slice(0, 5).map((activity) => (
                      <ActivityItem key={activity.id} activity={activity} variant="overdue" navigate={navigate} />
                    ))}
                  </div>
                </div>
              )}

              {/* Due Today */}
              {activities.due_today?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-amber-600 flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" />
                    Da fare oggi ({activities.due_today.length})
                  </h4>
                  <div className="space-y-2">
                    {activities.due_today.slice(0, 5).map((activity) => (
                      <ActivityItem key={activity.id} activity={activity} variant="today" navigate={navigate} />
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming */}
              {activities.upcoming?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-blue-600 flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4" />
                    Prossime attività ({activities.upcoming.length})
                  </h4>
                  <div className="space-y-2">
                    {activities.upcoming.slice(0, 5).map((activity) => (
                      <ActivityItem key={activity.id} activity={activity} variant="upcoming" navigate={navigate} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Channel - Pie */}
        {!isClient && (
          <Card className="animate-slide-up" style={{ animationDelay: "200ms" }}>
            <CardHeader>
              <CardTitle className="text-lg">Fatturato per Canale</CardTitle>
            </CardHeader>
            <CardContent>
              {channelData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={channelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {channelData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                  Nessun dato disponibile
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Revenue Trend - Line */}
        <Card className={`animate-slide-up ${isClient ? "lg:col-span-2" : ""}`} style={{ animationDelay: "250ms" }}>
          <CardHeader>
            <CardTitle className="text-lg">{isClient ? "Andamento Spesa" : "Andamento Fatturato"}</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v / 1000}k`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(24, 95%, 53%)"
                    strokeWidth={3}
                    dot={{ fill: "hsl(24, 95%, 53%)" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Admin/Agent Only */}
      {!isClient && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Clients - Bar */}
          <Card className="animate-slide-up" style={{ animationDelay: "300ms" }}>
            <CardHeader>
              <CardTitle className="text-lg">Top 5 Clienti</CardTitle>
            </CardHeader>
            <CardContent>
              {clientData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={clientData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v / 1000}k`} />
                    <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar dataKey="value" fill="hsl(173, 58%, 39%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                  Nessun dato disponibile
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue by Region - Bar */}
          <Card className="animate-slide-up" style={{ animationDelay: "350ms" }}>
            <CardHeader>
              <CardTitle className="text-lg">Fatturato per Regione</CardTitle>
            </CardHeader>
            <CardContent>
              {regionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={regionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 12 }} />
                    <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v / 1000}k`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar dataKey="value" fill="hsl(197, 37%, 24%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                  Nessun dato disponibile
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agent Performance - Admin Only */}
      {isAdmin && agentData.length > 0 && (
        <Card className="animate-slide-up" style={{ animationDelay: "400ms" }}>
          <CardHeader>
            <CardTitle className="text-lg">Performance Agenti</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v / 1000}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="revenue" fill="hsl(12, 76%, 61%)" name="Fatturato" radius={[4, 4, 0, 0]} />
                <Bar dataKey="commission" fill="hsl(43, 74%, 66%)" name="Provvigioni" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Activity Item Component
function ActivityItem({ activity, variant, navigate }) {
  const getActivityIcon = (type) => {
    switch (type) {
      case "call": return <Phone className="w-4 h-4" />;
      case "meeting": return <Calendar className="w-4 h-4" />;
      default: return <CheckCircle2 className="w-4 h-4" />;
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "overdue": return "bg-red-50 border-red-200 hover:bg-red-100";
      case "today": return "bg-amber-50 border-amber-200 hover:bg-amber-100";
      default: return "bg-blue-50 border-blue-200 hover:bg-blue-100";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  };

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${getVariantStyles()}`}
      onClick={() => activity.client_id && navigate(`/clients/${activity.client_id}`)}
    >
      <div className="flex items-center gap-3">
        <div className="text-muted-foreground">
          {getActivityIcon(activity.activity_type)}
        </div>
        <div>
          <p className="font-medium text-sm">{activity.title}</p>
          {activity.description && (
            <p className="text-xs text-muted-foreground line-clamp-1">{activity.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {activity.due_date && (
          <span className="text-xs text-muted-foreground">{formatDate(activity.due_date)}</span>
        )}
        <Badge variant="outline" className="text-xs capitalize">
          {activity.activity_type}
        </Badge>
      </div>
    </div>
  );
}
